import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { localize } from '../../i18n/locale';
import { getMyStats, syncPendingResults } from '../../client';
import { setToken } from '../../auth/auth';
import { buildApiUrl, getOauthLoginUrlWithReturn } from '../../config/runtime';
import {
    buildLoginUrlWithReturnTo,
    consumePendingReturnTo,
    getPostLoginRedirectPath,
    locationToReturnTo,
    savePendingReturnTo,
} from '../../auth/returnTo';
import {
    beginAuthFlow,
    clearAuthFlow,
    isNativeApp,
    openAuthSession,
    readPendingAuthFlow,
    STARTUS_AUTH_CALLBACK_URL,
} from '../../config/capacitor';

function emitAuthToast(message, tone = 'info') {
    window.dispatchEvent(new CustomEvent('auth-toast', { detail: { message, tone } }));
}

const AuthPage = () => {
    const { t, i18n } = useTranslation();
    const tr = (ko, en) => localize(i18n.resolvedLanguage || i18n.language, { ko, en });
    const [isLogin, setIsLogin] = useState(true);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        confirmPassword: ''
    });
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [socialPendingProvider, setSocialPendingProvider] = useState('');

    const navigate = useNavigate();
    const location = useLocation();

    const nativeMode = useMemo(() => isNativeApp(), []);

    useEffect(() => {
        setIsLogin(location.pathname !== '/signup');
        setMessage('');
        setFormData({ username: '', password: '', confirmPassword: '' });

        const pendingFlow = readPendingAuthFlow();
        if (nativeMode && pendingFlow?.status === 'pending') {
            setSocialPendingProvider(pendingFlow.provider || '');
            setMessage(tr('브라우저에서 로그인을 완료한 뒤 앱으로 돌아오세요.', 'Complete login in the browser, then return to the app.'));
            return;
        }

        if (pendingFlow?.status === 'failed' && pendingFlow?.lastError) {
            setMessage(pendingFlow.lastError);
            clearAuthFlow();
        }
    }, [location.pathname, nativeMode]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const toggleMode = () => {
        navigate(isLogin ? '/signup' : '/login', { state: location.state });
    };

    const finalizeLogin = async (token, fallbackPath = '/mypage', role = '') => {
        setToken(token);

        try {
            await syncPendingResults();
        } catch (syncError) {
            console.warn('Failed to sync pending results after login:', syncError);
        }

        const profile = await getMyStats();
        if (!profile) {
            emitAuthToast(tr('로그인은 완료됐지만 사용자 정보를 불러오지 못했습니다. 마이페이지에서 다시 시도해주세요.', 'Login completed, but your profile could not be loaded. Try again from My Page.'), 'warning');
        }

        if (role === 'admin') {
            consumePendingReturnTo('', '/mypage');
            navigate('/startus-admin', { replace: true });
            return;
        }

        const redirectPath = consumePendingReturnTo(fallbackPath, '/mypage');
        navigate(redirectPath, { replace: true });
    };

    const handleSocialLogin = async (provider) => {
        setMessage('');
        setLoading(true);

        try {
            const returnTo = getPostLoginRedirectPath({ location, fallback: '/mypage' });
            savePendingReturnTo(returnTo);

            const returnUrl = nativeMode
                ? `${STARTUS_AUTH_CALLBACK_URL}?returnTo=${encodeURIComponent(returnTo)}`
                : `${window.location.origin}${buildLoginUrlWithReturnTo(returnTo)}`;
            const flow = beginAuthFlow(provider, returnUrl, returnTo);
            const authUrl = getOauthLoginUrlWithReturn(provider, returnUrl, flow.stateNonce);

            setSocialPendingProvider(provider);
            await openAuthSession(authUrl);

            if (nativeMode) {
                setMessage(tr('브라우저에서 로그인을 완료한 뒤 앱으로 돌아오세요.', 'Complete login in the browser, then return to the app.'));
            }
        } catch (error) {
            console.error('Failed to start social login:', error);
            clearAuthFlow();
            setSocialPendingProvider('');
            setMessage(tr('소셜 로그인 창을 여는 데 실패했습니다. 다시 시도해주세요.', 'The social login window could not be opened. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setLoading(true);

        try {
            if (!isLogin) {
                if (formData.password !== formData.confirmPassword) {
                    throw new Error(t('auth.error_password_mismatch'));
                }

                const response = await fetch(buildApiUrl('/auth/signup'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username: formData.username, password: formData.password })
                });
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.detail || t('auth.fail_signup'));
                }

                alert(t('auth.success_signup'));
                navigate('/login', { state: location.state });
                return;
            }

            const loginFormData = new URLSearchParams();
            loginFormData.append('username', formData.username);
            loginFormData.append('password', formData.password);

            const response = await fetch(buildApiUrl('/auth/login'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: loginFormData
            });

            let data;
            try {
                data = await response.json();
            } catch (parseError) {
                console.error('Failed to parse login response:', parseError);
                if (response.status === 500) {
                    throw new Error(tr('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.', 'A server error occurred. Please try again shortly.'));
                }
                if (response.status === 503) {
                    throw new Error(tr('서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.', 'Could not connect to the server. Please try again shortly.'));
                }

                const text = await response.text();
                const snippet = String(text || '').slice(0, 200).trim();
                throw new Error(snippet ? `서버 응답이 올바르지 않습니다 (${response.status}): ${snippet}` : `서버 응답이 올바르지 않습니다 (${response.status})`);
            }

            if (!response.ok) {
                throw new Error(data?.detail || t('auth.fail_login'));
            }

            const fallbackPath = getPostLoginRedirectPath({ location, fallback: '/mypage' });
            savePendingReturnTo(fallbackPath || locationToReturnTo(location, '/mypage'));
            await finalizeLogin(data.access_token, fallbackPath, data.role);
        } catch (error) {
            console.error(error);
            const msg = String(error?.message || '');
            if (/Failed to fetch/i.test(msg) || /NetworkError/i.test(msg)) {
                setMessage(tr('서버 연결에 실패했습니다. 백엔드와 프록시 설정을 확인해주세요.', 'Server connection failed. Check the backend and proxy configuration.'));
            } else {
                setMessage(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.header}>
                    <h2 style={styles.title}>{isLogin ? t('auth.welcome_back') : t('auth.create_account')}</h2>
                    <p style={styles.subtitle}>{isLogin ? t('auth.subtitle_login') : t('auth.subtitle_signup')}</p>
                </div>

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.inputGroup}>
                        <input
                            type="text"
                            name="username"
                            placeholder={t('auth.username_placeholder', '아이디')}
                            value={formData.username}
                            onChange={handleChange}
                            style={styles.input}
                            required
                        />
                    </div>

                    <div style={styles.inputGroup}>
                        <input
                            type="password"
                            name="password"
                            placeholder={t('auth.password_placeholder', '비밀번호')}
                            value={formData.password}
                            onChange={handleChange}
                            style={styles.input}
                            required
                        />
                    </div>

                    {!isLogin && (
                        <div style={styles.inputGroup}>
                            <input type="password" name="confirmPassword" placeholder={t('auth.confirm_password_placeholder')} value={formData.confirmPassword} onChange={handleChange} style={styles.input} required />
                        </div>
                    )}

                    {message && <div style={styles.error}>{message}</div>}
                    {nativeMode && socialPendingProvider && (
                        <div style={styles.infoBox}>
                            {tr(`${socialPendingProvider} 로그인 대기 중입니다. 브라우저를 닫았거나 로그인을 취소했다면 다시 시도하세요.`, `Waiting for ${socialPendingProvider} login. Try again if you closed the browser or cancelled login.`)}
                        </div>
                    )}

                    <button type="submit" style={styles.submitButton} disabled={loading}>
                        {loading ? t('auth.processing') : (isLogin ? t('auth.login_button') : t('auth.signup_button'))}
                    </button>
                </form>

                <div style={styles.divider}>
                    <div style={styles.dividerLine}></div>
                    <span style={styles.dividerText}>{tr('또는 소셜 계정으로 로그인', 'Or continue with a social account')}</span>
                    <div style={styles.dividerLine}></div>
                </div>

                <div style={styles.socialGroup}>
                    <button
                        type="button"
                        style={{ ...styles.socialButton, ...styles.kakaoButton }}
                        onClick={() => handleSocialLogin('kakao')}
                        disabled={loading}
                    >
                        {tr('카카오로 시작하기', 'Continue with Kakao')}
                    </button>
                    <button
                        type="button"
                        style={{ ...styles.socialButton, ...styles.googleButton }}
                        onClick={() => handleSocialLogin('google')}
                        disabled={loading}
                    >
                        {tr('Google로 시작하기', 'Continue with Google')}
                    </button>
                </div>

                <div style={styles.footer}>
                    <p style={styles.footerText}>{isLogin ? t('auth.no_account') : t('auth.have_account')}</p>
                    <button type="button" onClick={toggleMode} style={styles.toggleButton}>
                        {isLogin ? t('auth.to_signup') : t('auth.to_login')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        padding: '2rem',
    },
    card: {
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '24px',
        padding: '3rem',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        textAlign: 'center',
        marginBottom: '2.5rem',
    },
    title: {
        color: '#fff',
        fontSize: '2rem',
        fontWeight: '700',
        marginBottom: '0.5rem',
        background: 'linear-gradient(to right, #60a5fa, #a78bfa)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    subtitle: {
        color: '#94a3b8',
        fontSize: '0.95rem',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.2rem',
        marginBottom: '1.5rem',
    },
    inputGroup: {
        position: 'relative',
    },
    input: {
        width: '100%',
        padding: '1rem 1.2rem',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(0, 0, 0, 0.2)',
        color: '#fff',
        fontSize: '1rem',
        transition: 'all 0.2s',
        outline: 'none',
        boxSizing: 'border-box'
    },
    submitButton: {
        width: '100%',
        padding: '1rem',
        borderRadius: '12px',
        border: 'none',
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        color: '#fff',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        marginTop: '0.5rem',
        transition: 'transform 0.1s, opacity 0.2s',
    },
    error: {
        color: '#ef4444',
        fontSize: '0.9rem',
        textAlign: 'center',
        background: 'rgba(239, 68, 68, 0.1)',
        padding: '0.8rem',
        borderRadius: '8px',
    },
    infoBox: {
        color: '#bfdbfe',
        fontSize: '0.9rem',
        textAlign: 'center',
        background: 'rgba(59, 130, 246, 0.12)',
        padding: '0.8rem',
        borderRadius: '8px',
        border: '1px solid rgba(96, 165, 250, 0.18)',
    },
    divider: {
        display: 'flex',
        alignItems: 'center',
        margin: '1.5rem 0',
    },
    dividerLine: {
        flex: 1,
        height: '1px',
        background: 'rgba(255, 255, 255, 0.1)',
    },
    dividerText: {
        color: '#94a3b8',
        padding: '0 1rem',
        fontSize: '0.85rem',
    },
    socialGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.8rem',
    },
    socialButton: {
        width: '100%',
        padding: '1rem',
        borderRadius: '12px',
        border: 'none',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.5rem',
        transition: 'transform 0.1s, opacity 0.2s',
    },
    kakaoButton: {
        background: '#FEE500',
        color: '#000000',
    },
    googleButton: {
        background: '#FFFFFF',
        color: '#3C4043',
        border: '1px solid #DADCE0',
    },
    footer: { marginTop: '2rem', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '0.5rem', alignItems: 'center' },
    footerText: { color: '#94a3b8', fontSize: '0.95rem', margin: 0 },
    toggleButton: { background: 'none', border: 'none', color: '#60a5fa', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer', padding: 0 }
};

export default AuthPage;
